import React, { useState, useEffect } from 'react';
import { BookOpen, ChevronDown, ChevronUp, Lock } from 'lucide-react';

/**
 * Component for selecting source books with ruleset-specific requirements
 */
const SourceSelector = ({ 
  sources = [], 
  selectedSources = {}, 
  toggleSource,
  ruleset // Added ruleset prop
}) => {
  const [isMinimized, setIsMinimized] = useState(false);
  
  // Ensure sources is an array
  const sourcesArray = Array.isArray(sources) ? sources : [];
  
  // Find PHB and XPHB sources
  const phbSource = sourcesArray.find(s => s.abbreviation === "PHB" || s.name.includes("Player's Handbook"));
  const xphbSource = sourcesArray.find(s => s.abbreviation === "XPHB" || s.name.includes("Player's Handbook (2024)"));
  
  // Handle source toggle with ruleset restrictions
  const handleToggleSource = (sourceId) => {
    // If PHB and ruleset is 2014 or 2024, don't allow deselection
    if (phbSource && sourceId === phbSource.id && (ruleset === '2014' || ruleset === '2024')) {
      return; // Prevent toggling off
    }
    
    // If XPHB and ruleset is 2024, don't allow deselection
    if (xphbSource && sourceId === xphbSource.id && ruleset === '2024') {
      return; // Prevent toggling off
    }
    
    // Otherwise allow normal toggle
    toggleSource(sourceId);
  };
  
  // Enforce ruleset rules on mount and when ruleset changes
  useEffect(() => {
    // Create a copy of current selections
    const updatedSources = {...selectedSources};
    let hasChanges = false;
    
    // For 2014 ruleset, ensure PHB is selected
    if (ruleset === '2014' && phbSource && !updatedSources[phbSource.id]) {
      updatedSources[phbSource.id] = true;
      hasChanges = true;
    }
    
    // For 2024 ruleset, ensure both PHB and XPHB are selected
    if (ruleset === '2024') {
      if (phbSource && !updatedSources[phbSource.id]) {
        updatedSources[phbSource.id] = true;
        hasChanges = true;
      }
      
      if (xphbSource && !updatedSources[xphbSource.id]) {
        updatedSources[xphbSource.id] = true;
        hasChanges = true;
      }
    }
    
    // If changes were made, update selected sources
    if (hasChanges) {
      // Handle selections outside component by calling the passed toggle for each required source
      if (phbSource && !selectedSources[phbSource.id] && (ruleset === '2014' || ruleset === '2024')) {
        toggleSource(phbSource.id);
      }
      
      if (xphbSource && !selectedSources[xphbSource.id] && ruleset === '2024') {
        toggleSource(xphbSource.id);
      }
    }
  }, [ruleset, sources, selectedSources, phbSource, xphbSource, toggleSource]);
  
  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
  };
  
  // Determine if a source is required based on ruleset
  const isSourceRequired = (source) => {
    if (!source) return false;
    
    if (phbSource && source.id === phbSource.id && (ruleset === '2014' || ruleset === '2024')) {
      return true;
    }
    
    if (xphbSource && source.id === xphbSource.id && ruleset === '2024') {
      return true;
    }
    
    return false;
  };
  
  return (
    <div className="bg-white p-6 mb-4 rounded-lg shadow-md">
      <div className="flex justify-between items-center cursor-pointer" onClick={toggleMinimize}>
        <h2 className="text-xl font-bold flex items-center">
          <BookOpen className="mr-2" />
          Source Books
        </h2>
        {isMinimized ? 
          <ChevronDown className="text-gray-600" /> : 
          <ChevronUp className="text-gray-600" />
        }
      </div>
      
      {!isMinimized && (
        <>
          <p className="text-gray-600 mt-4 mb-4">
            Select the source books to include:
            {ruleset === '2014' && (
              <span className="text-amber-600 ml-2 font-medium">
                (PHB required for 2014 ruleset)
              </span>
            )}
            {ruleset === '2024' && (
              <span className="text-amber-600 ml-2 font-medium">
                (PHB and XPHB required for 2024 ruleset)
              </span>
            )}
          </p>
          
          {sourcesArray.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
              {sourcesArray.map(source => {
                const isSelected = !!selectedSources[source.id];
                const required = isSourceRequired(source);
                
                return (
                  <button
                    key={source.id || `source-${Math.random()}`}
                    onClick={() => handleToggleSource(source.id)}
                    className={`py-2 px-3 text-sm rounded-md transition-colors duration-200 text-left flex justify-between items-center ${
                      isSelected 
                        ? 'bg-blue-500 text-white hover:bg-blue-600' 
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                    disabled={required}
                  >
                    <span>
                      {source.name || 'Unknown'} {source.abbreviation ? `(${source.abbreviation})` : ''}
                    </span>
                    {required && <Lock size={14} className="ml-1" />}
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="text-gray-500">No source books available.</p>
          )}
        </>
      )}
    </div>
  );
};

export default SourceSelector;
